// src/redux/slices/authSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth, googleProvider } from '../../firebase/config';
import toast from 'react-hot-toast';

const initialState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
  },
});

export const { setUser, setLoading, setError, clearError, logout } = authSlice.actions;

// Email/Password Sign Up
export const signUp = (email, password, displayName) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    dispatch(setUser({
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      displayName: displayName,
    }));
    toast.success(`Welcome ${displayName || email}! Account created successfully.`);
    return true;
  } catch (error) {
    let errorMessage = 'Sign up failed';
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'Email already in use';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters';
        break;
      default:
        errorMessage = error.message;
    }
    dispatch(setError(errorMessage));
    toast.error(errorMessage);
    return false;
  }
};

// Email/Password Login
export const login = (email, password) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    dispatch(setUser({
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      displayName: userCredential.user.displayName || email.split('@')[0],
    }));
    toast.success(`Welcome back ${userCredential.user.displayName || email.split('@')[0]}!`);
    return true;
  } catch (error) {
    let errorMessage = 'Login failed';
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'User not found';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Wrong password';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      default:
        errorMessage = error.message;
    }
    dispatch(setError(errorMessage));
    toast.error(errorMessage);
    return false;
  }
};

// Google Sign In
export const signInWithGoogle = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    dispatch(setUser({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || user.email.split('@')[0],
      photoURL: user.photoURL,
    }));
    toast.success(`Welcome ${user.displayName || user.email.split('@')[0]}!`);
    return true;
  } catch (error) {
    let errorMessage = 'Google sign in failed';
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        errorMessage = 'Sign in cancelled';
        break;
      case 'auth/account-exists-with-different-credential':
        errorMessage = 'Account exists with different credential';
        break;
      default:
        errorMessage = error.message;
    }
    dispatch(setError(errorMessage));
    toast.error(errorMessage);
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await signOut(auth);
    dispatch(logout());
    toast.success('Logged out successfully');
    return true;
  } catch (error) {
    toast.error('Logout failed');
    return false;
  }
};

export const resetPassword = (email) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await sendPasswordResetEmail(auth, email);
    toast.success('Password reset email sent!');
    return true;
  } catch (error) {
    toast.error(error.message);
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export default authSlice.reducer;